'use client';

import { Link } from '@/i18n/routing';
import { useTranslations, useLocale } from 'next-intl';
import { useEffect } from 'react';

export default function PingwinPassPage() {
  const t = useTranslations('pingwinPassPage');
  const locale = useLocale();

  useEffect(() => {
    document.title = t('meta.title');
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', t('meta.description'));
    }
  }, [t]);

  return (
    <div className="content-page">
      <section className="hero">
        <div className="hero-background">
          <div
            className="hero-image"
            style={{
              backgroundImage: 'linear-gradient(135deg, rgba(16, 185, 129, 0.9), rgba(5, 150, 105, 0.85)), url(/api/placeholder/1920/600)'
            }}
          ></div>
        </div>
        <div className="hero-content">
          <h1 className="hero-title">{t('hero.title')}</h1>
          <p className="hero-subtitle">
            {t('hero.subtitle')}
          </p>
        </div>
      </section>

      <main className="content-main">
        <div className="content-container">

          {/* Hero CTA */}
          <div className="cta-box cta-primary">
            <h2>{t('heroCta.title')}</h2>
            <p>{t('heroCta.description')}</p>
            <a
              href="https://grupapingwina.e-skipass.pl"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="btn btn-primary"
            >
              {t('heroCta.button')}
            </a>
          </div>

          {/* Main Content */}
          <article className="content-article">

            <section>
              <h2>{t('whatIs.title')}</h2>
              <p>
                {t('whatIs.paragraph1')}
              </p>
              <p>
                {t('whatIs.paragraph2')}
              </p>
            </section>

            <section>
              <h2>{t('types.title')}</h2>

              <div className="difficulty-grid">
                <div className="difficulty-card green">
                  <div className="difficulty-icon">⛷️</div>
                  <h3>{t('types.ski.title')}</h3>
                  <p>
                    {t('types.ski.description')}
                  </p>
                  <ul className="park-features">
                    <li>{t('types.ski.feature1')}</li>
                    <li>{t('types.ski.feature2')}</li>
                    <li>{t('types.ski.feature3')}</li>
                  </ul>
                </div>

                <div className="difficulty-card blue">
                  <div className="difficulty-icon">🚵</div>
                  <h3>{t('types.bike.title')}</h3>
                  <p>
                    {t('types.bike.description')}
                  </p>
                  <ul className="park-features">
                    <li>{t('types.bike.feature1')}</li>
                    <li>{t('types.bike.feature2')}</li>
                    <li>{t('types.bike.feature3')}</li>
                  </ul>
                </div>

                <div className="difficulty-card red">
                  <div className="difficulty-icon">🎿🚴</div>
                  <h3>{t('types.skiBike.title')}</h3>
                  <p>
                    {t('types.skiBike.description')}
                  </p>
                  <ul className="park-features">
                    <li>{t('types.skiBike.feature1')}</li>
                    <li>{t('types.skiBike.feature2')}</li>
                    <li>{t('types.skiBike.feature3')}</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2>{t('bikeDetails.title')}</h2>

              <div className="parks-highlight">
                <div className="park-card">
                  <h3>{t('bikeDetails.bikeparks.title')}</h3>
                  <p>
                    {t('bikeDetails.bikeparks.description')}
                  </p>
                  <ul className="park-features">
                    <li>{t('bikeDetails.bikeparks.park1')}</li>
                    <li>{t('bikeDetails.bikeparks.park2')}</li>
                    <li>{t('bikeDetails.bikeparks.park3')}</li>
                    <li>{t('bikeDetails.bikeparks.park4')}</li>
                  </ul>
                </div>

                <div className="park-card">
                  <h3>{t('bikeDetails.pumptracks.title')}</h3>
                  <p>
                    {t('bikeDetails.pumptracks.description')}
                  </p>
                  <ul className="park-features">
                    <li>{t('bikeDetails.pumptracks.track1')}</li>
                    <li>{t('bikeDetails.pumptracks.track2')}</li>
                  </ul>
                </div>

                <div className="park-card">
                  <h3>{t('bikeDetails.validity.title')}</h3>
                  <p>
                    {t('bikeDetails.validity.description')}
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2>{t('benefits.title')}</h2>

              <div className="tips-list">
                <div className="tip-item">
                  <h3>{t('benefits.benefit1.title')}</h3>
                  <p>
                    {t('benefits.benefit1.description')}
                  </p>
                </div>

                <div className="tip-item">
                  <h3>{t('benefits.benefit2.title')}</h3>
                  <p>
                    {t('benefits.benefit2.description')}
                  </p>
                </div>

                <div className="tip-item">
                  <h3>{t('benefits.benefit3.title')}</h3>
                  <p>
                    {t('benefits.benefit3.description')}
                  </p>
                </div>

                <div className="tip-item">
                  <h3>{t('benefits.benefit4.title')}</h3>
                  <p>
                    {t('benefits.benefit4.description')}
                  </p>
                </div>

                <div className="tip-item">
                  <h3>{t('benefits.benefit5.title')}</h3>
                  <p>
                    {t('benefits.benefit5.description')}
                  </p>
                </div>

                <div className="tip-item">
                  <h3>{t('benefits.benefit6.title')}</h3>
                  <p>
                    {t('benefits.benefit6.description')}
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2>{t('pricing.title')}</h2>
              <p>
                {t('pricing.description')}
              </p>

              <div className="tips-list">
                <div className="tip-item">
                  <h3>{t('pricing.toddler.title')}</h3>
                  <p>
                    {t('pricing.toddler.description')}
                  </p>
                </div>

                <div className="tip-item">
                  <h3>{t('pricing.child.title')}</h3>
                  <p>
                    {t('pricing.child.description')}
                  </p>
                </div>

                <div className="tip-item">
                  <h3>{t('pricing.junior.title')}</h3>
                  <p>
                    {t('pricing.junior.description')}
                  </p>
                </div>

                <div className="tip-item">
                  <h3>{t('pricing.adult.title')}</h3>
                  <p>
                    {t('pricing.adult.description')}
                  </p>
                </div>
              </div>

              <div className="tip-box">
                <strong>{t('pricing.note.label')}</strong> {t('pricing.note.text')}
              </div>
            </section>

            <section>
              <h2>{t('howToBuy.title')}</h2>
              <p>
                {t('howToBuy.description')}
              </p>

              <div className="steps-list">
                <div className="step-item">
                  <div className="step-number">1</div>
                  <div>
                    <h3>{t('howToBuy.step1.title')}</h3>
                    <p>
                      {t('howToBuy.step1.description')}
                    </p>
                  </div>
                </div>

                <div className="step-item">
                  <div className="step-number">2</div>
                  <div>
                    <h3>{t('howToBuy.step2.title')}</h3>
                    <p>
                      {t('howToBuy.step2.description')}
                    </p>
                  </div>
                </div>

                <div className="step-item">
                  <div className="step-number">3</div>
                  <div>
                    <h3>{t('howToBuy.step3.title')}</h3>
                    <p>
                      {t('howToBuy.step3.description')}
                    </p>
                  </div>
                </div>

                <div className="step-item">
                  <div className="step-number">4</div>
                  <div>
                    <h3>{t('howToBuy.step4.title')}</h3>
                    <p>
                      {t('howToBuy.step4.description')}
                    </p>
                  </div>
                </div>

                <div className="step-item">
                  <div className="step-number">5</div>
                  <div>
                    <h3>{t('howToBuy.step5.title')}</h3>
                    <p>
                      {t('howToBuy.step5.description')}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2>{t('forWhom.title')}</h2>

              <div className="parks-highlight">
                <div className="park-card">
                  <h3>{t('forWhom.riders.title')}</h3>
                  <p>
                    {t('forWhom.riders.description')}
                  </p>
                </div>

                <div className="park-card">
                  <h3>{t('forWhom.families.title')}</h3>
                  <p>
                    {t('forWhom.families.description')}
                  </p>
                </div>

                <div className="park-card">
                  <h3>{t('forWhom.learners.title')}</h3>
                  <p>
                    {t('forWhom.learners.description')}
                  </p>
                </div>
              </div>
            </section>

          </article>

          {/* Bottom CTA */}
          <div className="cta-box cta-secondary">
            <h2>{t('bottomCta.title')}</h2>
            <p>{t('bottomCta.description')}</p>
            <div className="cta-buttons">
              <a
                href="https://grupapingwina.e-skipass.pl"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="btn btn-primary"
              >
                {t('bottomCta.button1')}
              </a>
              <Link href="/bikeparki" className="btn btn-secondary">
                {t('bottomCta.button2')}
              </Link>
            </div>
          </div>

          {/* Related Links */}
          <div className="related-links">
            <h3>{t('relatedLinks.title')}</h3>
            <ul>
              <li><Link href="/przewodnik">{t('relatedLinks.link1')}</Link></li>
              <li><Link href="/jak-zaczac">{t('relatedLinks.link2')}</Link></li>
              <li><Link href="/bikeparki/lista">{t('relatedLinks.link3')}</Link></li>
            </ul>
          </div>

        </div>
      </main>
    </div>
  );
}
